import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { axPerfLogger } from '@theme/performance';
import { getCartManager } from '@theme/cart-manager';

/**
 * Cart Drawer Upsell Component
 *
 * Renders product recommendations inside the cart drawer: the first one as a featured
 * card (`[ref="featured"]`), the next ones as a Swiper carousel. With the block setting
 * "featured_first" unchecked, all of them go to the carousel.
 *
 * - Dynamic mode: fetches recommendations from Shopify's Product Recommendations
 *   API via Section Rendering. Falls back to a curated collection.
 * - Collection mode: server-rendered only, JS still inits Swiper for the slider UI.
 *
 * The SSR fallback (`[data-ax-fallback]`) renders raw product-cards. On init,
 * we move them into `<swiper-slide>` elements inside the `<swiper-container>`,
 * then call `.initialize()` programmatically (same pattern as ax-tabbed-product-lists).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} featured
 * @property {HTMLElement} productsContainer
 * @property {HTMLElement & { swiper?: any, initialize?: () => void }} swiper
 * @property {HTMLElement} fallback
 * @property {HTMLElement} arrows
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class CartDrawerUpsell extends Component {
  /** @type {string} */
  #currentProductId = '';

  /** @type {boolean} */
  #hasFetchedInitial = false;

  /** @type {boolean} */
  #swiperReady = false;

  /** @type {number} */
  #fetchId = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#currentProductId = this.dataset.productId || '';
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Init Swiper from whatever SSR fallback rendered (collection mode, recommendations
    // already performed on this page, etc.). Empty in dynamic-mode initial render —
    // we'll re-init after the fetch fills the fallback.
    this.#initSwiper();

    if (Shopify.designMode) {
      if (this.#currentProductId) {
        this.#hasFetchedInitial = true;
        this.#fetchAndRender(this.#currentProductId);
      }
    } else if (this.#currentProductId) {
      this.#hasFetchedInitial = true;
      this.#fetchAndRender(this.#currentProductId);
    } else {
      document.addEventListener('ax:cart-drawer-opened', this.#handleDrawerOpen, { once: true });
    }
  }

  updatedCallback() {
    super.updatedCallback();

    const newProductId = this.dataset.productId || '';
    if (!newProductId) return;

    if (newProductId !== this.#currentProductId) {
      this.#currentProductId = newProductId;
      this.#fetchAndRender(newProductId);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener('ax:cart-drawer-opened', this.#handleDrawerOpen);
    this.#destroySwiper();
  }

  #handleDrawerOpen = () => {
    if (this.#hasFetchedInitial || !this.#currentProductId) return;
    this.#hasFetchedInitial = true;
    this.#fetchAndRender(this.#currentProductId);
  };

  /**
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    const detail =
      /** @type {{ detail?: { data?: { productId?: string, source?: string, didError?: boolean } } }} */
      (event);

    if (detail.detail?.data?.didError) return;

    // Added from this block: keep the seed (the device the list was built for) and refetch,
    // so the product just added drops out (the section skips cart products). Reseeding on it
    // would show its own recommendations, or keep the stale list when it has none.
    if (event.target instanceof Node && this.contains(event.target)) {
      this.#scrollDrawerToTop();
      if (this.#currentProductId) this.#fetchAndRender(this.#currentProductId, { clearIfEmpty: true });
      return;
    }

    const addedProductId = detail.detail?.data?.productId;
    const newId = addedProductId ? addedProductId.toString() : this.#getLatestMainProductId();
    if (!newId) return;

    if (newId !== this.#currentProductId) {
      this.#currentProductId = newId;
      this.#fetchAndRender(newId);
    }
  };

  /**
   * Brings the top of the drawer back into view after an add from this block: the new
   * line lands there (cart items are newest first) while the block sits at the bottom.
   * @private
   */
  #scrollDrawerToTop() {
    const scroller = this.closest('.cart-drawer__content');
    if (!scroller) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scroller.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /**
   * Most recently added main product still in the cart (cart items are newest first).
   * Mirrors the seed rules of cart-drawer-upsell.liquid.
   * @returns {string}
   */
  #getLatestMainProductId() {
    return this.#getMainProductIds()[0] ?? '';
  }

  /**
   * Main products of the cart, newest first, without duplicates.
   * Same skips as the seed rules of cart-drawer-upsell.liquid.
   * @private
   * @returns {string[]}
   */
  #getMainProductIds() {
    const items =
      /** @type {Array<{ product_id: number, properties?: Record<string, string> | null, parent_relationship?: object | null }> | undefined} */
      (getCartManager().cartJSON?.items);
    if (!items) return [];

    const mainItems = items.filter(({ properties, parent_relationship }) => {
      const props = properties ?? {};
      if (props._bundle_role && props._bundle_role !== 'main') return false;
      if (props._subscription_term || parent_relationship) return false;
      return !props._sample && !props._gift_wrap && !props._Gifted;
    });

    return [...new Set(mainItems.map(({ product_id }) => product_id.toString()))];
  }

  /**
   * Fetches recommendations via Section Rendering and re-renders the featured card,
   * the slides and Swiper.
   *
   * `intent=complementary` returns *only* the products curated in Search &
   * Discovery, and an empty list for products with no curation. So when the
   * primary intent yields no product we retry once with `related` rather than
   * leaving the block empty.
   *
   * @param {string} productId
   * @param {{ clearIfEmpty?: boolean }} [options] - clearIfEmpty: empty the block when
   *   nothing is left to recommend (after an add from it), instead of keeping the current list
   */
  async #fetchAndRender(productId, { clearIfEmpty = false } = {}) {
    const baseUrl = this.dataset.url;
    const sectionId = this.dataset.sectionId;
    if (!baseUrl || !productId || !sectionId) return;

    const fetchId = ++this.#fetchId;
    const intent = this.dataset.intent || 'complementary';
    const perfOp = axPerfLogger.start('upsell-fetch', { productId, intent });
    this.#setLoading(true);

    try {
      perfOp.mark('fetch-start');
      let products = await this.#fetchProducts(baseUrl, productId, sectionId, intent);
      perfOp.mark('html-parsed');

      if (this.#isEmpty(products) && intent === 'complementary') {
        products = await this.#fetchProducts(baseUrl, productId, sectionId, 'related');
        perfOp.mark('fallback-parsed');
      }

      // A seed with no recommendation at all (an accessory added from this block, then a
      // page reload makes it the newest line): fall back on the other cart products,
      // newest first, instead of keeping a stale list or an empty block.
      if (this.#isEmpty(products)) {
        for (const fallbackId of this.#getMainProductIds()) {
          if (fallbackId === productId) continue;
          products = await this.#fetchProducts(baseUrl, fallbackId, sectionId, intent);
          if (!this.#isEmpty(products)) break;
        }
        perfOp.mark('cart-fallback-parsed');
      }

      if (fetchId !== this.#fetchId) return;

      if (this.#isEmpty(products)) {
        if (clearIfEmpty) {
          this.#replaceFeatured(null);
          this.#replaceSlides([]);
        }
        return;
      }

      this.#replaceFeatured(products.featured);
      this.#replaceSlides(products.slides);
      perfOp.mark('slides-replaced');
    } catch {
      // Keep current state
    } finally {
      if (fetchId === this.#fetchId) this.#setLoading(false);
      perfOp.end();
    }
  }

  /**
   * Dims the carousel and shows the loader while recommendations are refetched.
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    this.refs.productsContainer?.setAttribute('aria-busy', String(isLoading));
  }

  /**
   * Requests one recommendations intent and extracts the rendered featured card and slides.
   * @param {string} baseUrl
   * @param {string} productId
   * @param {string} sectionId
   * @param {string} intent
   * @returns {Promise<{ featured: HTMLElement | null, slides: HTMLElement[] }>}
   */
  async #fetchProducts(baseUrl, productId, sectionId, intent) {
    const url = `${baseUrl}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    const response = await fetch(url);
    if (!response.ok) return { featured: null, slides: [] };

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    return {
      featured: doc.querySelector('.cart-drawer-upsell__featured-card'),
      slides: /** @type {HTMLElement[]} */ ([
        ...doc.querySelectorAll('.cart-drawer-upsell__fallback .cart-drawer-upsell__slide'),
      ]),
    };
  }

  /**
   * True when a recommendations response holds no product at all.
   * @param {{ featured: HTMLElement | null, slides: HTMLElement[] }} products
   * @returns {boolean}
   */
  #isEmpty({ featured, slides }) {
    return !featured && slides.length === 0;
  }

  /**
   * Swaps the featured card for the freshly rendered one, or removes it when the
   * response has none (setting "featured_first" unchecked: classic carousel only).
   * @param {HTMLElement | null} card
   */
  #replaceFeatured(card) {
    this.refs.featured?.replaceChildren(...(card ? [card] : []));
  }

  /**
   * Replaces existing slides with fresh ones. Reuses the existing Swiper
   * instance via removeAllSlides() + appendSlide() so the carousel config
   * (slidesPerView, spaceBetween, peek) survives the refresh.
   * If Swiper isn't initialised yet (dynamic mode first fetch), bootstrap via
   * the fallback path instead.
   * @param {HTMLElement[]} newSlides
   */
  #replaceSlides(newSlides) {
    const swiper = this.#getSwiper();

    if (!swiper) {
      // First fetch in dynamic mode — populate the fallback then init Swiper.
      const fallback = this.querySelector('[data-ax-fallback]');
      if (fallback instanceof HTMLElement) {
        fallback.replaceChildren(...newSlides);
        this.#initSwiper();
      }
      return;
    }

    // Wrap each raw .cart-drawer-upsell__slide in <swiper-slide> for Swiper API
    const wrapped = newSlides.map((slide) => {
      const ss = document.createElement('swiper-slide');
      ss.appendChild(slide);
      return ss;
    });

    swiper.removeAllSlides();
    if (wrapped.length > 0) swiper.appendSlide(wrapped);
    swiper.update();

    this.#syncArrowsVisibility(wrapped.length);
    this.#syncArrowsState();
  }

  /**
   * Wraps SSR fallback slides in <swiper-slide>, configures Swiper, and initializes it.
   */
  async #initSwiper() {
    if (this.#swiperReady) return;

    const swiperEl = this.querySelector('swiper-container.cart-drawer-upsell__swiper');
    const fallback = this.querySelector('[data-ax-fallback]');
    if (!(swiperEl instanceof HTMLElement) || !(fallback instanceof HTMLElement)) return;

    const rawSlides = [...fallback.children];
    if (rawSlides.length === 0) {
      // No slides yet (dynamic mode, no fetch performed) — hide arrows + leave Swiper
      // un-initialised. We'll re-enter this method after #fetchAndRender finishes.
      this.#syncArrowsVisibility(0);
      return;
    }

    await customElements.whenDefined('swiper-container');

    for (const slide of rawSlides) {
      const swiperSlide = document.createElement('swiper-slide');
      swiperSlide.appendChild(slide);
      swiperEl.appendChild(swiperSlide);
    }
    fallback.remove();

    const swiperContainer =
      /** @type {HTMLElement & { swiper?: any, initialize?: () => void }} */
      (swiperEl);

    Object.assign(swiperContainer, {
      // Mobile default: 1.4 → 1 card + ~40% of the next peeking on 375px width.
      // Desktop (>=750px): 2.3 → 2 cards + ~30% peek inside the 480px drawer.
      slidesPerView: 1.4,
      slidesPerGroup: 1,
      spaceBetween: 12,
      speed: 400,
      grabCursor: true,
      watchOverflow: true,
      a11y: true,
      keyboard: { enabled: true },
      breakpoints: {
        750: { slidesPerView: 2.3, spaceBetween: 12 },
      },
    });

    swiperContainer.initialize?.();
    swiperContainer.removeAttribute('init');
    this.#swiperReady = true;

    const swiper = swiperContainer.swiper;
    if (!swiper) return;

    this.#bindArrows(swiper);
    this.#syncArrowsVisibility(rawSlides.length);
  }

  /**
   * on:click handler for the "previous" slideshow-arrow.
   */
  previous() {
    this.#getSwiper()?.slidePrev();
  }

  /**
   * on:click handler for the "next" slideshow-arrow.
   */
  next() {
    this.#getSwiper()?.slideNext();
  }

  #getSwiper() {
    const swiperEl =
      /** @type {(HTMLElement & { swiper?: any }) | null} */
      (this.querySelector('swiper-container.cart-drawer-upsell__swiper'));
    return swiperEl?.swiper ?? null;
  }

  /**
   * Binds disabled-state sync between Swiper and the slideshow-arrow refs.
   * @param {any} swiper
   */
  #bindArrows(swiper) {
    this.#syncArrowsState();

    for (const event of ['slideChange', 'reachBeginning', 'reachEnd', 'fromEdge', 'resize']) {
      swiper.on(event, this.#syncArrowsState);
    }
  }

  /**
   * Pushes the current Swiper position onto the arrows' disabled state and the mask.
   *
   * Swiper only emits its position events on user-driven moves, so a programmatic
   * refresh of the slide set leaves the arrows frozen: `removeAllSlides()` empties
   * the carousel, which disables "next", and `update()` emits none of the bound
   * events once the new slides land. Any programmatic change to the slides must
   * therefore call this explicitly, on top of the event bindings.
   * @private
   */
  #syncArrowsState = () => {
    const swiper = this.#getSwiper();
    if (!swiper) return;

    const { previous, next } = /** @type {Refs} */ (this.refs);
    if (previous) previous.disabled = Boolean(swiper.isBeginning);
    if (next) next.disabled = Boolean(swiper.isEnd);
    this.#syncMask(swiper);
  };

  /**
   * Updates the CSS mask on the products container based on swiper position.
   * Fades the left edge when scrolled past the start, fades the right edge
   * when not yet at the end — providing a visual hint for more content.
   * @param {any} swiper
   */
  #syncMask(swiper) {
    const container = /** @type {HTMLElement | undefined} */ (this.refs.productsContainer);
    if (!container) return;

    const atStart = Boolean(swiper.isBeginning);
    const atEnd = Boolean(swiper.isEnd);

    if (atStart && atEnd) {
      container.style.maskImage = 'none';
    } else if (atStart) {
      container.style.maskImage = 'linear-gradient(to right, black 90%, transparent 100%)';
    } else if (atEnd) {
      container.style.maskImage = 'linear-gradient(to right, transparent 0%, black 10%)';
    } else {
      container.style.maskImage = 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)';
    }
  }

  /**
   * Hides the arrows when there aren't enough slides to slide.
   * @param {number} slideCount
   */
  #syncArrowsVisibility(slideCount) {
    const arrows = /** @type {HTMLElement | undefined} */ (this.refs.arrows);
    if (!arrows) return;
    // 2 slides visible → arrows useful only when 3+ products
    arrows.hidden = slideCount < 3;
  }

  #destroySwiper() {
    const swiperEl =
      /** @type {(HTMLElement & { swiper?: any }) | null} */
      (this.querySelector('swiper-container.cart-drawer-upsell__swiper'));
    swiperEl?.swiper?.destroy(true, true);
    this.#swiperReady = false;
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
